/**
 * authResponse.ts — the no-credential discriminator, as a pure leaf.
 *
 * WHY IT LIVES ALONE
 * ==================
 * "We never asked" and "the server said no" are different facts, and only one is
 * evidence about the session. Every caller needs to tell them apart — which means the
 * predicate has to be importable from anywhere, including a jest suite with no React
 * Native runtime.
 *
 * It cannot live in `fetchWithAuth`. That module reaches `tokenManager` ->
 * `secureTokenStorage` -> `expo-secure-store`, which ships untransformed ESM; importing
 * it at module load makes any consumer's test suite fail to parse. That is exactly what
 * happened when `services/api.ts` first re-exported the predicate: five health suites
 * stopped running, not because anything was wrong with them, but because a shared
 * wrapper had quietly dragged a native module into their import graph.
 *
 * ZERO IMPORTS IS THE POINT. Keep it that way.
 */

/**
 * Header stamped on the synthetic 401 that `fetchWithAuth` returns when there is no
 * credential to send — i.e. when NO request was made.
 *
 * Production evidence for why this distinction is load-bearing (3-day CloudWatch sweep,
 * 2026-08-19): `AUTH_401 reason=missing_token` 242 events vs `reason=expired` 117. The
 * dominant server-side rejection is a client asking with no Authorization header at all.
 */
export const NO_CREDENTIAL_HEADER = 'X-Cedar-No-Credential';

/**
 * True when this Response was produced WITHOUT a request being made, because no
 * credential was available.
 *
 * A caller seeing `true` knows nothing about its session: the server was never asked. It
 * must NOT sign the user out, delete queued data, or render "this was removed". Retrying
 * once a credential exists is the correct response.
 *
 * Safe on anything — a real server response, a test fake, or junk. An absent header
 * simply reads false.
 */
export function isNoCredentialResponse(res: Response | null | undefined): boolean {
  if (!res || typeof (res as any).headers?.get !== 'function') return false;
  return res.headers.get(NO_CREDENTIAL_HEADER) === '1';
}
